/**
 * Full comic-book issues (5–6 pages each) that dramatize the content of a
 * real Canales exam. Each page is a comic layout with narrator captions,
 * character speech bubbles, and an optional "lesson" callout that ties back
 * to the exam. The reader turns pages one by one and a CTA at the end sends
 * the vendor to take the linked exam.
 */

export const COMICS = [
  /* ─────────────────────  ISSUE 5 · MANAGER  ─────────────────────
     Six-chapter leadership comic that maps 1-to-1 with the Manager
     Checklist PDF: Ventas · Servicio · Inventario · Financiamiento ·
     Liderazgo · Marketing. Featured character: Carlos as store manager.
  ------------------------------------------------------------------- */
  {
    slug: "manager",
    exam_slug: "manager", // exact slug filled by backend seed; findComic uses this too
    title: { es: "Carlos toma la tienda", en: "Carlos takes the store" },
    subtitle: {
      es: "El día que Carlos pasó de vendedor a manager y aprendió las 6 áreas.",
      en: "The day Carlos went from salesperson to manager and mastered the 6 areas.",
    },
    cover_color: "#0d1a3f",
    accent: "#FFD100",
    category: "manager",
    pages: [
      {
        layout: "cover",
        title: { es: "Carlos toma la tienda", en: "Carlos takes the store" },
        subtitle: {
          es: "Liderar, motivar, ejecutar, crecer.",
          en: "Lead, motivate, execute, grow.",
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 1 — Ventas: alinear al equipo", en: "Ch. 1 — Sales: align the team" },
        caption: {
          es: "9:00 AM. Carlos junta al equipo antes de abrir. Meta clara, zonas asignadas.",
          en: "9:00 AM. Carlos huddles the team before opening. Clear target, zones assigned.",
        },
        panels: [
          { speaker: "carlos", bg: "#eaf3ff", text: { es: "Meta hoy: $18k. Ana en la entrada, Mario financiamiento, yo Tempur.", en: "Today's target: $18k. Ana up front, Mario financing, me Tempur." } },
          { speaker: "ana", bg: "#ffefef", text: { es: "¡Listos! ¿Cuál es el spiff de hoy?", en: "Ready! What's today's spiff?" } },
          { speaker: "narrator", bg: "#fff", text: { es: "Un buen manager alinea al equipo ANTES de abrir la puerta. Nadie vende solo, todos venden juntos.", en: "A good manager aligns the team BEFORE opening the door. Nobody sells alone, everyone sells together." } },
        ],
        lesson: {
          variant: "did_you_know",
          title: { es: "REGLA DEL MANAGER", en: "MANAGER'S RULE" },
          text: {
            es: "El huddle de 5 minutos vale más que 5 juntas de 1 hora.",
            en: "A 5-minute huddle beats five 1-hour meetings.",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 2 — Servicio: la queja como regalo", en: "Ch. 2 — Service: a complaint is a gift" },
        caption: {
          es: "2:14 PM. Una clienta furiosa. Carlos se planta con calma.",
          en: "2:14 PM. A furious customer. Carlos steps in calmly.",
        },
        panels: [
          { speaker: "carlos", bg: "#eaf3ff", text: { es: "Señora, la escucho. Tiene toda la razón. Me disculpo en nombre de Canales.", en: "Ma'am, I'm listening. You're absolutely right. I apologize on behalf of Canales." } },
          { speaker: "narrator", bg: "#fff", text: { es: "Escuchar → Disculparse → Resolver → Follow-up. Un cliente rescatado vale 10 nuevos.", en: "Listen → Apologize → Solve → Follow-up. A rescued customer is worth 10 new ones." } },
          { speaker: "ana", bg: "#ffefef", text: { es: "¿Anoto en el registro semanal para revisar tiempos de resolución?", en: "Should I log this for the weekly resolution-time review?" } },
        ],
        lesson: {
          variant: "important",
          title: { es: "KPI SEMANAL", en: "WEEKLY KPI" },
          text: {
            es: "El manager mide tiempo de resolución + estrellas de Google cada 7 días.",
            en: "The manager tracks resolution time + Google star rating every 7 days.",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 3 — Inventario: la verdad importa", en: "Ch. 3 — Inventory: truth matters" },
        caption: {
          es: "3 sofás azules en el sistema. Cero en piso. Carlos investiga sin drama.",
          en: "3 blue sofas in the system. Zero on the floor. Carlos investigates calmly.",
        },
        panels: [
          { speaker: "carlos", bg: "#eaf3ff", text: { es: "Almacén: 2 sí, 1 no. Actualizo el sistema y bajamos 2 al piso hoy.", en: "Warehouse: 2 yes, 1 no. Updating system and putting 2 on the floor today." } },
          { speaker: "ana", bg: "#ffefef", text: { es: "¿Y el conteo cíclico semanal? ¿Toca esta categoría?", en: "And the weekly cycle count? Is this the right category?" } },
          { speaker: "narrator", bg: "#fff", text: { es: "Cada lunes, una categoría rotativa. En 2 meses la tienda entera queda auditada sin cerrar un día.", en: "Every Monday, a rotating category. In 2 months the whole store is audited without closing." } },
        ],
        lesson: {
          variant: "remember",
          title: { es: "NUNCA VENDAS LO QUE NO TIENES", en: "NEVER SELL WHAT YOU DON'T HAVE" },
          text: {
            es: "Sistema ≠ piso = venta cancelada + reseña negativa. El conteo cíclico lo previene.",
            en: "System ≠ floor = canceled sale + bad review. Cycle counting prevents it.",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 4 — Financiamiento: rescatar al 30%", en: "Ch. 4 — Financing: rescue 30%" },
        caption: {
          es: "Un cliente no pasa Synchrony. Carlos NO lo deja ir sin ofrecer la segunda opción.",
          en: "A customer doesn't get Synchrony. Carlos does NOT let them leave without the backup.",
        },
        panels: [
          { speaker: "carlos", bg: "#eaf3ff", text: { es: "Señor, hoy Synchrony no pasó — pero tengo otro plan que sí. ¿Le hago la solicitud?", en: "Sir, Synchrony didn't approve today — but I have another plan that will. Shall I apply?" } },
          { speaker: "ana", bg: "#ffefef", text: { es: "¡Aprobó! Y con el plan Allstate quedan protegidos los muebles.", en: "Approved! And with Allstate, the furniture is protected." } },
          { speaker: "narrator", bg: "#fff", text: { es: "Segunda oportunidad + protección = 30% de ventas rescatadas cada mes.", en: "Second-chance + protection plan = 30% of sales rescued every month." } },
        ],
        lesson: {
          variant: "did_you_know",
          title: { es: "META DEL EQUIPO", en: "TEAM TARGET" },
          text: {
            es: "Aprobación Synchrony ≥ 50%. Debajo de eso, entrena el pitch mañana mismo.",
            en: "Synchrony approval ≥ 50%. Below that, drill the pitch tomorrow.",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 5 — Liderazgo: data, no gritos", en: "Ch. 5 — Leadership: data, not shouts" },
        caption: {
          es: "Diego pide aumento. Carlos abre el dashboard, no la puerta.",
          en: "Diego asks for a raise. Carlos opens the dashboard, not the door.",
        },
        panels: [
          { speaker: "carlos", bg: "#eaf3ff", text: { es: "Diego, tu enero fue $9k, meta $12k. Cierra $14k en febrero y hablamos del aumento.", en: "Diego, your January was $9k, target $12k. Close $14k in February and we discuss the raise." } },
          { speaker: "narrator", bg: "#fff", text: { es: "Reconocer en público, corregir en privado. Meta clara + revisión mensual = mérito, no favores.", en: "Praise in public, correct in private. Clear target + monthly review = merit, not favors." } },
          { speaker: "ana", bg: "#ffefef", text: { es: "El pizarrón 'Cierre del día' hace vibrar a la tienda. Sana competencia.", en: "The 'Close of the day' whiteboard makes the store buzz. Healthy competition." } },
        ],
        lesson: {
          variant: "important",
          title: { es: "REGLA DE ORO", en: "GOLDEN RULE" },
          text: {
            es: "Regañar en piso rompe confianza. Coachear en privado la construye.",
            en: "Public scolding breaks trust. Private coaching builds it.",
          },
        },
      },
      {
        layout: "big-lesson",
        title: { es: "Cap. 6 — Marketing: el momento pico", en: "Ch. 6 — Marketing: the peak moment" },
        caption: {
          es: "La familia González sale feliz. Ana no los deja ir sin pedir la reseña.",
          en: "The González family leaves happy. Ana doesn't let them leave without asking for a review.",
        },
        panels: [
          { speaker: "ana", bg: "#eaf3ff", text: { es: "Señora, ¿nos deja una reseñita en Google? Con foto de su sofá vale doble.", en: "Ma'am, could you leave us a quick Google review? A photo of your sofa doubles it." } },
          { speaker: "narrator", bg: "#fff", text: { es: "Reseñas + Marketplace + referidos = 80% del tráfico nuevo. El manager lo mide cada semana.", en: "Reviews + Marketplace + referrals = 80% of new traffic. Manager tracks it weekly." } },
        ],
        cta: {
          es: "Liderar, Motivar, Ejecutar, Crecer. Esas son las 4 palabras del manager Canales.",
          en: "Lead, Motivate, Execute, Grow. Those are the 4 words of a Canales manager.",
        },
        lesson: {
          variant: "remember",
          title: { es: "LA REGLA DE CARLOS", en: "CARLOS' RULE" },
          text: {
            es: "El manager no vende, hace que TODO el equipo venda. Su score es el score del equipo.",
            en: "A manager doesn't sell — they make the WHOLE team sell. Their score is the team's score.",
          },
        },
      },
    ],
  },
];

const OTHER_COMICS_START = [
  /* ─────────────────────  ISSUE 6 · ALLSTATE  ─────────────────────
     10-page bilingual comic packed with REAL data from the Allstate
     Store Launch Training PDF: statistics, coverage matrix, day-1 perks,
     3 R's fulfillment, repair-cost benchmarks, claim journey (3 options),
     Furniture Standard vs Premium and the 180-day second-sale window.
  ------------------------------------------------------------------- */
  {
    slug: "allstate",
    exam_slug: "allstate",
    title: { es: "Allstate: You're in Good Hands", en: "Allstate: You're in Good Hands" },
    subtitle: {
      es: "El plan de protección que cambió la forma en que la gente piensa sobre planes.",
      en: "The protection plan that changed how people think about protection plans.",
    },
    cover_color: "#0033A0",
    accent: "#F97316",
    category: "allstate",
    pages: [
      {
        layout: "cover",
        title: { es: "Allstate: You're in Good Hands", en: "Allstate: You're in Good Hands" },
        subtitle: {
          es: "80% de los americanos confían. 160 millones de pólizas. 99.4% resueltas sin problema.",
          en: "80% of Americans trust it. 160M policies. 99.4% resolved incident-free.",
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 1 — La marca que la gente conoce", en: "Ch. 1 — The brand people know" },
        caption: {
          es: "Ana le explica a la familia González por qué Allstate no es 'un plan más'.",
          en: "Ana explains to the González family why Allstate isn't 'just another plan'.",
        },
        panels: [
          { speaker: "ana", bg: "#eaf3ff", text: { es: "80% de los americanos conocen Allstate. Es la aseguradora que gasta $1B al año en publicidad.", en: "80% of Americans know Allstate. They spend $1B per year in advertising." } },
          { speaker: "carlos", bg: "#fff8dc", text: { es: "Y 76% de los clientes prefieren Allstate sobre cualquier otro plan de protección.", en: "And 76% of customers prefer Allstate over any other protection plan." } },
          { speaker: "narrator", bg: "#fff", text: { es: "SquareTrade nació en 1999. Allstate la adquirió en 2017. Hoy: 160 millones de pólizas activas y 10,000 reclamos procesados cada mes.", en: "SquareTrade founded 1999. Allstate acquired it in 2017. Today: 160M active policies and 10,000 claims processed monthly." } },
        ],
        lesson: {
          variant: "did_you_know",
          title: { es: "DATO CLAVE", en: "KEY STAT" },
          text: {
            es: "99.4% de los reclamos se resuelven sin incidentes. Tu cliente está en buenas manos, literalmente.",
            en: "99.4% of claims resolved without incidents. Your customer is in good hands — literally.",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 2 — Desde el DÍA 1", en: "Ch. 2 — From DAY 1" },
        caption: {
          es: "El vendedor confundido cree que el plan 'entra al año'. FALSO. Estos son los 8 beneficios desde el minuto uno.",
          en: "Confused vendors think the plan 'kicks in after year 1'. FALSE. Here are the 8 Day-1 perks.",
        },
        panels: [
          { speaker: "ana", bg: "#eaf3ff", text: { es: "Sobrecarga eléctrica cubierta hoy mismo. Soporte técnico 24/7 SIN costo. Crédito de $300 por comida si se te descompone el refri.", en: "Electrical surge covered today. 24/7 tech support at NO cost. $300 food credit if your fridge fails." } },
          { speaker: "carlos", bg: "#fff8dc", text: { es: "Y $50 por ropa si la lavadora te deja tirado un lunes. Cero deducible, cero cargos ocultos, cero envío.", en: "And $50 for laundry if the washer bails on you on Monday. Zero deductible, zero hidden fees, zero shipping." } },
          { speaker: "narrator", bg: "#fff", text: { es: "Extra: 180 días de ventana de compra (segunda oportunidad), garantía de servicio en 5 días para electrodomésticos, plan transferible SIN costo si vendes el producto.", en: "Bonus: 180-day purchase window (second sales chance), 5-day service guarantee for majors, plan TRANSFERABLE at no cost if you sell the item." } },
        ],
        lesson: {
          variant: "important",
          title: { es: "SEGUNDA OPORTUNIDAD", en: "SECOND CHANCE" },
          text: {
            es: "Si el cliente dijo 'no' al plan, tienes 180 días para volver a ofrecerlo. Rescata ventas perdidas.",
            en: "If the customer said 'no' to the plan, you have 180 days to re-offer it. Rescue lost sales.",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 3 — La línea de tiempo (5 años)", en: "Ch. 3 — The 5-year timeline" },
        caption: {
          es: "El cliente compra su lavadora. Así corre la protección en paralelo con la garantía de fábrica.",
          en: "The customer buys their washer. Here's how coverage runs in parallel with the manufacturer's warranty.",
        },
        panels: [
          { speaker: "narrator", bg: "#fff", text: { es: "AÑO 1: falla mecánica/eléctrica la cubre el fabricante. Si el fabricante niega, Allstate se hace cargo (¡doble red de seguridad!).", en: "YEAR 1: mechanical/electrical failure covered by manufacturer. If they deny it, Allstate steps in (double safety net!)." } },
          { speaker: "narrator", bg: "#fff", text: { es: "AÑOS 2-3: 100% Allstate. Servicio a domicilio, piezas overnight, sin deducible.", en: "YEARS 2-3: 100% Allstate. In-home service, overnight parts, no deductible." } },
          { speaker: "narrator", bg: "#fff", text: { es: "AÑOS 4-5 (planes de 5 años): Allstate sigue cubriendo. Tech support + surge + créditos activos los 5 años completos.", en: "YEARS 4-5 (5-year plans): Allstate keeps covering. Tech support + surge + credits stay active all 5 years." } },
        ],
        lesson: {
          variant: "remember",
          title: { es: "PITCH DE UN LÍNEA", en: "ONE-LINE PITCH" },
          text: {
            es: "'Con el plan estás cubierto 5 años completos, sin deducibles, y transferible si vendes el mueble.'",
            en: "'With the plan you're covered a full 5 years, no deductibles, transferable if you sell the piece.'",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 4 — Muebles: Standard vs Premium", en: "Ch. 4 — Furniture: Standard vs Premium" },
        caption: {
          es: "El Plan de Muebles cubre 5 años. Standard cuida lo básico. Premium suma mascotas, chapa, espejos y limpieza.",
          en: "The Furniture Plan covers 5 years. Standard handles the basics. Premium adds pets, veneer, mirrors and cleaning.",
        },
        panels: [
          { speaker: "ana", bg: "#eaf3ff", text: { es: "Standard: manchas accidentales, rasgaduras, quemaduras, costuras rotas, vidrio astillado, herrajes.", en: "Standard: accidental stains, tears, burns, broken seams, chipped glass, hardware." } },
          { speaker: "carlos", bg: "#fff8dc", text: { es: "Premium suma: mascotas (1 vez), chapa/veneer, espejos desplateados y — bonus — limpieza profesional incluida.", en: "Premium adds: pet damage (1×), veneer/chapado, resilvered mirrors and — bonus — professional cleaning included." } },
          { speaker: "narrator", bg: "#fff", text: { es: "Bonus extra: kits de limpieza de manchas enviados overnight a la puerta del cliente. Sin llamar técnico, cliente feliz.", en: "Extra bonus: overnight spot-cleaning kits shipped to the customer's door. No technician call, happy customer." } },
        ],
        lesson: {
          variant: "did_you_know",
          title: { es: "PITCH PREMIUM", en: "PREMIUM PITCH" },
          text: {
            es: "'¿Tiene perro o gato? Le recomiendo Premium — cubre el daño por mascota UNA vez, gratis.'",
            en: "'Do you have a dog or cat? I recommend Premium — it covers pet damage ONCE, free.'",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 5 — Cubierto ✓ vs NO cubierto ✗", en: "Ch. 5 — Covered ✓ vs NOT covered ✗" },
        caption: {
          es: "La regla es simple: si la falla afecta la FUNCIÓN y no fue por accidente ni negligencia → cubierto.",
          en: "Simple rule: if the failure affects FUNCTION and wasn't accident or negligence → covered.",
        },
        panels: [
          { speaker: "ana", bg: "#eaf3ff", text: { es: "✓ Perilla de estufa que gira suelta. ✓ Manija del refri que se cae. ✓ Empaque suelto en lavadora frontal.", en: "✓ Stove knob turning loose. ✓ Fridge handle falling off. ✓ Loose gasket on front-load washer." } },
          { speaker: "carlos", bg: "#fff8dc", text: { es: "✗ Cristal roto por caída. ✗ Grasa que atora la perilla. ✗ Tupperware derretido sobre el quemador. ✗ Abolladura por golpe.", en: "✗ Glass broken by drop. ✗ Grease stuck knob. ✗ Melted Tupperware on burner. ✗ Dent from hit." } },
          { speaker: "narrator", bg: "#fff", text: { es: "Excluye: daño cosmético que no afecta función, condiciones preexistentes, daño en transporte o instalación, pérdida o robo.", en: "Excludes: cosmetic damage that doesn't affect function, pre-existing conditions, transport/install damage, loss or theft." } },
        ],
        lesson: {
          variant: "important",
          title: { es: "LA REGLA DE ORO", en: "GOLDEN RULE" },
          text: {
            es: "Si el cliente pregunta '¿esto está cubierto?' → ¿afecta la función? ¿fue accidente? Con eso ya sabes.",
            en: "If a customer asks 'is this covered?' → does it affect function? Was it accidental? That's all you need.",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 6 — El reclamo: las 3 R's", en: "Ch. 6 — The claim: the 3 R's" },
        caption: {
          es: "Cuando algo falla, Allstate NO da vueltas. Tres opciones, un resultado: cliente feliz.",
          en: "When something fails, Allstate DOESN'T stall. Three options, one outcome: happy customer.",
        },
        panels: [
          { speaker: "ana", bg: "#eaf3ff", text: { es: "REPARAR: técnico en casa en 5 días hábiles. REEMPLAZAR: producto igual o mejor. REEMBOLSAR: precio total sin deducciones.", en: "REPAIR: in-home tech within 5 business days. REPLACE: equal or better product. REIMBURSE: full price with no deductions." } },
          { speaker: "carlos", bg: "#fff8dc", text: { es: "Y hay POLÍTICA NO LEMON: si el mismo problema se repite, Allstate reemplaza. Punto.", en: "And there's a NO LEMON POLICY: if the same problem repeats, Allstate replaces. Period." } },
          { speaker: "narrator", bg: "#fff", text: { es: "3 formas de abrir el reclamo (24/7): App SquareTrade, sitio allstateprotectionplans.com/service, o teléfono (877) 971-0013. 25% se resuelve en el diagnóstico.", en: "3 ways to file (24/7): SquareTrade App, allstateprotectionplans.com/service, or phone (877) 971-0013. 25% resolved during diagnostic." } },
        ],
        lesson: {
          variant: "remember",
          title: { es: "CERO FRICCIÓN", en: "ZERO FRICTION" },
          text: {
            es: "$0 deducible + partes overnight + cita en 5 días + no lemon = razón #1 por la que la gente repite compra.",
            en: "$0 deductible + overnight parts + 5-day appointment + no lemon = the #1 reason people come back to buy.",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 7 — El costo REAL de reparar sin plan", en: "Ch. 7 — REAL cost of repair without a plan" },
        caption: {
          es: "Los americanos gastan $68 BILLONES al año reparando electrodomésticos. Estos son los números que le enseñas al cliente.",
          en: "Americans spend $68 BILLION a year fixing appliances. These are the numbers you show the customer.",
        },
        panels: [
          { speaker: "ana", bg: "#eaf3ff", text: { es: "Compresor de refri: $500 a $1,000. Motor de lavadora: $300 a $500. Placa de control de horno: $200 a $600.", en: "Fridge compressor: $500-$1,000. Washer motor: $300-$500. Oven control board: $200-$600." } },
          { speaker: "carlos", bg: "#fff8dc", text: { es: "Un solo reclamo cubierto por Allstate PAGA el plan completo — y sobra. Es matemática simple, no venta.", en: "One single Allstate-covered claim PAYS for the whole plan — with change. Simple math, not sales." } },
          { speaker: "narrator", bg: "#fff", text: { es: "Ejemplo Sub-Zero: cubre 2-3 años, Allstate suma piezas + soporte técnico + crédito comida. Y con el Plan de 10 Años de Componentes cubres UN componente principal hasta $500 hasta el año 10.", en: "Sub-Zero example: covers 2-3 years, Allstate adds parts + tech support + food credit. With the 10-Year Major Component Plan you cover ONE major part up to $500 through year 10." } },
        ],
        lesson: {
          variant: "did_you_know",
          title: { es: "ARGUMENTO CIERRA-VENTA", en: "DEAL-CLOSING ARGUMENT" },
          text: {
            es: "'El plan cuesta menos que el 10% de una sola reparación de compresor. Con UNA falla ya lo pagó.'",
            en: "'The plan costs less than 10% of one compressor repair. ONE failure and it pays for itself.'",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 8 — El correo del cliente y el registro", en: "Ch. 8 — Customer email & registration" },
        caption: {
          es: "El paso que 8 de cada 10 vendedores olvidan — y le cuesta al cliente la experiencia completa.",
          en: "The step 8 out of 10 vendors forget — and it costs the customer the whole experience.",
        },
        panels: [
          { speaker: "ana", bg: "#eaf3ff", text: { es: "Cuando cargues el pedido en el portal, SIEMPRE captura el correo REAL del cliente. Nunca uses el correo de la tienda.", en: "When you upload the order to the portal, ALWAYS capture the customer's REAL email. Never use the store email." } },
          { speaker: "carlos", bg: "#fff8dc", text: { es: "Allstate le manda contrato digital en segundos. Si ya tiene app, el plan se agrega solo a su cuenta.", en: "Allstate sends the digital contract in seconds. If they have the app, the plan is auto-added to their account." } },
          { speaker: "narrator", bg: "#fff", text: { es: "El cliente NO necesita registrarse, pero si lo hace y sube su recibo, el reclamo futuro tarda 3 minutos en vez de 15. Regálale ese tip.", en: "The customer doesn't NEED to register, but if they do and upload the receipt, a future claim takes 3 minutes instead of 15. Give them that tip." } },
        ],
        lesson: {
          variant: "important",
          title: { es: "SI OLVIDAS ESTO", en: "IF YOU FORGET THIS" },
          text: {
            es: "Sin correo real = cliente no recibe contrato = queja + reseña negativa. Es el error #1 en Allstate.",
            en: "No real email = customer gets no contract = complaint + bad review. It's the #1 mistake with Allstate.",
          },
        },
      },
      {
        layout: "big-lesson",
        title: { es: "Cap. 9 — Cierra con confianza", en: "Ch. 9 — Close with confidence" },
        caption: {
          es: "La familia González duda. Ana no baja el precio: baja el miedo.",
          en: "The González family hesitates. Ana doesn't drop the price — she drops the fear.",
        },
        panels: [
          { speaker: "ana", bg: "#eaf3ff", text: { es: "'Señores, con Allstate su sofá está protegido 5 años: manchas, rasgaduras, mascotas, todo. Menos que un café al mes.'", en: "'Folks, with Allstate your sofa is protected 5 years: stains, tears, pets, everything. Less than a coffee a month.'" } },
          { speaker: "narrator", bg: "#fff", text: { es: "Si dicen 'lo pensamos', recuerda: tienes 180 DÍAS para volver a ofrecerlo. Anota su compra y llámalos al día 30 con la segunda oportunidad.", en: "If they say 'let us think', remember: you have 180 DAYS to re-offer. Log their purchase and call at day 30 with the second chance." } },
        ],
        cta: {
          es: "You're in Good Hands. Con Allstate el cliente vuelve — y trae a su familia contigo.",
          en: "You're in Good Hands. With Allstate the customer comes back — and brings their family.",
        },
        lesson: {
          variant: "remember",
          title: { es: "MANTRA ALLSTATE", en: "ALLSTATE MANTRA" },
          text: {
            es: "Repara, Reemplaza, Reembolsa. $0 deducible. 5 años. 180 días para re-ofrecer. You're in Good Hands.",
            en: "Repair, Replace, Reimburse. $0 deductible. 5 years. 180 days to re-offer. You're in Good Hands.",
          },
        },
      },
    ],
  },
  /* ─────────────────────  ISSUE 1 · VENTAS  ───────────────────── */
  {
    slug: "ventas",
    exam_slug: "sales-ffb137",
    title: { es: "El día que Ana cerró 3 sofás", en: "The day Ana closed 3 sofas" },
    subtitle: {
      es: "Una historia de saludo, descubrimiento y cierre",
      en: "A story of greeting, discovery and closing",
    },
    cover_color: "#005BBB",
    accent: "#FFD100",
    category: "sales",
    pages: [
      {
        layout: "cover",
        title: {
          es: "El día que Ana cerró 3 sofás",
          en: "The day Ana closed 3 sofas",
        },
        subtitle: {
          es: "Cómo Ana pasó de vender uno al mes a vender tres en una tarde.",
          en: "How Ana went from one sale a month to three in one afternoon.",
        },
      },
      {
        layout: "panels-2",
        title: { es: "Cap. 1 — La familia González entra", en: "Ch. 1 — The González family walks in" },
        caption: {
          es: "8:47 AM. Muebleria Canales. La familia González entra a la tienda con una sonrisa cansada.",
          en: "8:47 AM. Muebleria Canales. The González family walks in with a tired smile.",
        },
        scenes: [
          {
            prompt: "Wide shot: the González family (Hispanic parents + boy and girl) walking through the glass entrance of the Muebleria Canales showroom, tired but smiling. Comic-book style.",
            panel: {
              speaker: "ana",
              text: {
                es: "¡Buenos días! Soy Ana, mucho gusto. ¿Cómo se llaman?",
                en: "Good morning! I'm Ana, nice to meet you. What are your names?",
              },
              tail: "left",
            },
          },
          {
            prompt: "Close-up: dad exhaling in relief, mom smiling shyly, kids peeking. Ana in red polo across from them, extending an open hand. Warm Canales showroom.",
            caption: {
              es: "El papá suelta el aire. Ya no se sienten \"vendidos\". Se sienten recibidos.",
              en: "Dad exhales. They no longer feel \"sold to\". They feel welcomed.",
            },
          },
        ],
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.6,
            text: {
              es: "¡Buenos días! Soy Ana, mucho gusto. ¿Cómo se llaman?",
              en: "Good morning! I'm Ana, nice to meet you. What are your names?",
            },
            action: {
              es: "Ana sonríe, mira a los ojos y NO dice \"¿te puedo ayudar?\".",
              en: "Ana smiles, makes eye contact and does NOT say \"can I help you?\".",
            },
          },
          {
            speaker: "narrator",
            bg: "#fff",
            text: {
              es: "El papá suelta el aire. Ya no se sienten \"vendidos\". Se sienten recibidos.",
              en: "Dad exhales. They no longer feel \"sold to\". They feel welcomed.",
            },
          },
        ],
        lesson: {
          variant: "did_you_know",
          title: { es: "¿SABÍAS QUÉ?", en: "DID YOU KNOW?" },
          text: {
            es: "El saludo con tu nombre baja la guardia del cliente en 5 segundos. La pregunta genérica la sube.",
            en: "Greeting with your name lowers a customer's guard in 5 seconds. A generic question raises it.",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 2 — Descubrir, no vender", en: "Ch. 2 — Discover, don't sell" },
        caption: {
          es: "En vez de mostrar precios, Ana hace preguntas abiertas.",
          en: "Instead of showing prices, Ana asks open questions.",
        },
        scenes: [
          {
            prompt: "Medium shot: Ana crouches to the kids' eye level, tablet in hand, asking questions to the mom. Warm lighting, comic-book style.",
            panel: {
              speaker: "ana",
              text: {
                es: "¿Para qué habitación buscan el mueble? ¿Cuántos son en la casa?",
                en: "Which room is it for? How many live at home?",
              },
              tail: "left",
            },
          },
          {
            prompt: "Close-up: Carlos, the mentor in blue polo, giving a thumbs up while watching Ana in the background. Comic-book style.",
            panel: {
              speaker: "carlos",
              text: {
                es: "¡Correcto! Preguntas abiertas: para, cómo, cuántos. Nunca sí/no.",
                en: "Nailed it! Open questions: which, how, how many. Never yes/no.",
              },
              tail: "right",
            },
          },
          {
            prompt: "Cute portrait: the son Diego, eyes lighting up, talking about movies. Ana in the background writing on tablet. Comic-book style.",
            caption: {
              es: "Diego dice: \"vemos películas los viernes\". Ana anota. Esa palabra vale oro.",
              en: "Diego says: \"we watch movies on Fridays\". Ana notes it. Golden word.",
            },
          },
        ],
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.4,
            text: {
              es: "¿Para qué habitación buscan el mueble? ¿Cuántos son en la casa?",
              en: "Which room is this for? How many live at home?",
            },
          },
          {
            speaker: "carlos",
            bg: "#ffefef",
            tilt: 0.6,
            text: {
              es: "¡Correcto! Preguntas abiertas: para, cómo, cuántos. Nunca sí/no.",
              en: "Nailed it! Open questions: which, how, how many. Never yes/no.",
            },
          },
          {
            speaker: "narrator",
            bg: "#fff",
            text: {
              es: "El hijo, Diego, contesta: \"vemos películas los viernes\". Ana anota. Esa palabra vale oro.",
              en: "Their son Diego says: \"we watch movies on Fridays\". Ana notes it. That word is gold.",
            },
          },
        ],
        lesson: {
          variant: "remember",
          title: { es: "RECUERDA", en: "REMEMBER" },
          text: {
            es: "80% de la venta ocurre cuando escuchas más de lo que hablas.",
            en: "80% of the sale happens when you listen more than you talk.",
          },
        },
      },
      {
        layout: "panels-2",
        title: { es: "Cap. 3 — F.A.B. en acción", en: "Ch. 3 — F.A.B. in action" },
        caption: {
          es: "Ana no dice \"mira este sofá\". Ana traduce cada característica a un beneficio para SU familia.",
          en: "Ana doesn't say \"look at this sofa\". Ana translates every feature into a benefit for THEIR family.",
        },
        scenes: [
          {
            prompt: "Ana pointing at a modern blue sofa with waterproof-fabric detail, family observing. Cutaway water-drop icon floating above showing the fabric repelling liquid. Comic-book style.",
            panel: {
              speaker: "ana",
              text: {
                es: "Tela impermeable (F). No se mancha con jugo ni palomitas (A). Noches de película sin miedos (B).",
                en: "Waterproof fabric (F). No juice or popcorn stains (A). Worry-free movie nights (B).",
              },
              tail: "left",
            },
          },
          {
            prompt: "Wide happy panel: the boy Diego bouncing on the blue sofa, mom sitting comfortably touching the fabric, dad smiling with arms crossed. Ana stands proud beside them. Comic-book style.",
            caption: {
              es: "La mamá se sienta, toca la tela, sonríe. Diego brinca encima. Nadie se enoja. El sofá aguanta.",
              en: "Mom sits, touches the fabric, smiles. Diego bounces on it. No scolding. The sofa holds.",
            },
          },
        ],
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.5,
            text: {
              es: "Este sofá tiene tela impermeable (F). No se mancha con jugo ni palomitas (A). Sus noches de películas serán sin miedos (B).",
              en: "This sofa has waterproof fabric (F). Juice and popcorn can't stain it (A). Your movie nights will be worry-free (B).",
            },
          },
          {
            speaker: "narrator",
            bg: "#fff",
            text: {
              es: "La mamá se sienta, toca la tela, sonríe. Diego brinca encima. Nadie se enoja. El sofá aguanta.",
              en: "Mom sits, touches the fabric, smiles. Diego bounces on it. Nobody scolds. The sofa holds.",
            },
          },
        ],
        lesson: {
          variant: "important",
          title: { es: "F.A.B.", en: "F.A.B." },
          text: {
            es: "Feature → Advantage → Benefit. El beneficio es lo único que compra el cliente.",
            en: "Feature → Advantage → Benefit. The benefit is the only thing the customer buys.",
          },
        },
      },
      {
        layout: "panels-2",
        title: { es: "Cap. 4 — El cierre alternativo", en: "Ch. 4 — The alternative close" },
        caption: {
          es: "La familia duda. Ana no baja el precio. Ana da opciones.",
          en: "The family hesitates. Ana does not drop the price. Ana offers options.",
        },
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.3,
            text: {
              es: "¿Se los entregamos el jueves o el sábado? Y aparte, con nuestro plan de $99 al mes ya lo estrenan este fin.",
              en: "Should we deliver Thursday or Saturday? And with our $99/month plan you can enjoy it this weekend.",
            },
          },
          {
            speaker: "carlos",
            bg: "#ffefef",
            tilt: 0.5,
            text: {
              es: "¡Cierre alternativo perfecto! Nunca preguntes \"sí o no\". Da dos caminos que ganan.",
              en: "Perfect alternative close! Never ask \"yes or no\". Give two winning paths.",
            },
          },
        ],
        lesson: {
          variant: "common_error",
          title: { es: "ERROR COMÚN", en: "COMMON MISTAKE" },
          text: {
            es: "Preguntar \"¿lo quiere sí o no?\" activa la defensa. Da opciones, no ultimátums.",
            en: "Asking \"do you want it yes or no?\" triggers defense. Give options, not ultimatums.",
          },
        },
      },
      {
        layout: "big-lesson",
        title: { es: "Cap. 5 — Follow-up de campeones", en: "Ch. 5 — Champions follow up" },
        caption: {
          es: "48 horas después, Ana llama para saber cómo van con el sofá. La mamá le pide otro para la abuela.",
          en: "48 hours later, Ana calls to check in. Mom asks for a second sofa for grandma.",
        },
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.4,
            text: {
              es: "Hola señora González, ¿cómo van con el sofá? ¿Diego ya lo estrenó con palomitas?",
              en: "Hi Mrs. González, how's the sofa? Did Diego break it in with popcorn yet?",
            },
          },
        ],
        cta: {
          es: "3 sofás vendidos en una tarde. No fue suerte: fue GREET → DISCOVER → F.A.B. → CLOSE → FOLLOW-UP.",
          en: "3 sofas sold in one afternoon. Not luck: GREET → DISCOVER → F.A.B. → CLOSE → FOLLOW-UP.",
        },
        lesson: {
          variant: "remember",
          title: { es: "LA REGLA DE ANA", en: "ANA'S RULE" },
          text: {
            es: "Un cliente feliz trae 3 amigos más. El follow-up puede TRIPLICAR tus ventas.",
            en: "A happy customer brings 3 more friends. Follow-ups can TRIPLE your sales.",
          },
        },
      },
    ],
  },

  /* ─────────────────────  ISSUE 2 · SERVICIO  ───────────────────── */
  {
    slug: "servicio",
    exam_slug: "service-441467",
    title: { es: "Carlos y la queja imposible", en: "Carlos and the impossible complaint" },
    subtitle: {
      es: "Una queja es un regalo. Escucha, disculpa, resuelve.",
      en: "A complaint is a gift. Listen, apologize, solve.",
    },
    cover_color: "#E3000F",
    accent: "#0d1a3f",
    category: "service",
    pages: [
      {
        layout: "cover",
        title: {
          es: "Carlos y la queja imposible",
          en: "Carlos and the impossible complaint",
        },
        subtitle: {
          es: "Cómo convertir un cliente furioso en el más fiel de tu vida.",
          en: "How to turn a furious customer into your most loyal fan.",
        },
      },
      {
        layout: "panels-2",
        title: { es: "Cap. 1 — La cliente furiosa", en: "Ch. 1 — The angry customer" },
        caption: {
          es: "Sábado, 2:14 PM. La señora Ramírez entra hecha una tormenta. Su sillón llegó con una raya.",
          en: "Saturday, 2:14 PM. Mrs. Ramírez storms in. Her recliner arrived with a scratch.",
        },
        panels: [
          {
            speaker: "narrator",
            bg: "#ffefef",
            text: {
              es: "La cliente golpea el mostrador. Todos los ojos se voltean. Carlos respira profundo.",
              en: "The customer slams the counter. Every eye turns. Carlos takes a deep breath.",
            },
          },
          {
            speaker: "carlos",
            bg: "#eaf3ff",
            tilt: -0.4,
            text: {
              es: "Señora Ramírez, la escucho. Cuénteme todo lo que pasó, no me apure.",
              en: "Mrs. Ramírez, I'm listening. Tell me everything, take your time.",
            },
          },
        ],
        lesson: {
          variant: "important",
          title: { es: "IMPORTANTE", en: "IMPORTANT" },
          text: {
            es: "Antes de resolver, ESCUCHA sin interrumpir. El cliente necesita sentirse oído.",
            en: "Before you solve, LISTEN without interrupting. The customer needs to feel heard.",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 2 — La disculpa real", en: "Ch. 2 — The real apology" },
        caption: {
          es: "No hay excusas. Carlos no culpa a la fábrica ni al chofer. Carlos se hace cargo.",
          en: "No excuses. Carlos doesn't blame the factory or the driver. Carlos owns it.",
        },
        panels: [
          {
            speaker: "carlos",
            bg: "#eaf3ff",
            tilt: -0.5,
            text: {
              es: "Tiene toda la razón. No es lo que le prometimos. Me disculpo en nombre de Canales.",
              en: "You're absolutely right. This isn't what we promised. I apologize on behalf of Canales.",
            },
          },
          {
            speaker: "narrator",
            bg: "#fff",
            text: {
              es: "La cliente baja los hombros. Alguien la escuchó. Alguien no la ignoró.",
              en: "The customer's shoulders drop. Someone listened. Someone didn't ignore her.",
            },
          },
          {
            speaker: "carlos",
            bg: "#eaf3ff",
            tilt: 0.6,
            text: {
              es: "Vamos a hacer esto: cambio inmediato del sillón + 15% de descuento en su próxima compra.",
              en: "Here's what we'll do: immediate replacement + 15% off your next purchase.",
            },
          },
        ],
        lesson: {
          variant: "did_you_know",
          title: { es: "¿SABÍAS QUÉ?", en: "DID YOU KNOW?" },
          text: {
            es: "El 70% de los clientes que se quejan Y se sienten escuchados se vuelven fieles PARA SIEMPRE.",
            en: "70% of complaining customers who feel heard become loyal FOR LIFE.",
          },
        },
      },
      {
        layout: "panels-2",
        title: { es: "Cap. 3 — Resolver de verdad", en: "Ch. 3 — Actually solve it" },
        caption: {
          es: "Carlos no promete lo que no puede cumplir. Carlos promete lo que va a cumplir HOY.",
          en: "Carlos doesn't promise what he can't deliver. He promises what he WILL deliver today.",
        },
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.3,
            text: {
              es: "Señora, este es el número directo del gerente de entregas. Llamamos ahora mismo delante de usted.",
              en: "Ma'am, this is the delivery manager's direct number. We're calling right now, in front of you.",
            },
          },
          {
            speaker: "narrator",
            bg: "#fff",
            text: {
              es: "La transparencia mata la furia. Ver la solución en vivo transforma la queja en confianza.",
              en: "Transparency kills anger. Watching the solution unfold turns a complaint into trust.",
            },
          },
        ],
        lesson: {
          variant: "remember",
          title: { es: "RECUERDA", en: "REMEMBER" },
          text: {
            es: "Nunca prometas plazos que no controlas. Promete el siguiente paso concreto.",
            en: "Never promise timelines you don't control. Promise the next concrete step.",
          },
        },
      },
      {
        layout: "big-lesson",
        title: { es: "Cap. 4 — Un mes después", en: "Ch. 4 — One month later" },
        caption: {
          es: "La señora Ramírez volvió. Compró la sala completa. Y trajo a su hermana.",
          en: "Mrs. Ramírez came back. Bought the entire living room set. And brought her sister.",
        },
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.4,
            text: {
              es: "Carlos, la señora Ramírez está pidiendo por ti. Trajo a su hermana y a su vecina.",
              en: "Carlos, Mrs. Ramírez is asking for you. She brought her sister and her neighbor.",
            },
          },
        ],
        cta: {
          es: "Escuchar → Disculparse → Resolver → Follow-up. Esa es la fórmula que convierte quejas en clientes de por vida.",
          en: "Listen → Apologize → Solve → Follow-up. That's the formula that turns complaints into lifelong customers.",
        },
        lesson: {
          variant: "important",
          title: { es: "LA REGLA DE CARLOS", en: "CARLOS' RULE" },
          text: {
            es: "Una queja bien resuelta vale más que 10 ventas fáciles. Fideliza para siempre.",
            en: "A complaint well handled is worth more than 10 easy sales. It bonds for life.",
          },
        },
      },
    ],
  },

  /* ─────────────────────  ISSUE 3 · FINANCIAMIENTO  ───────────────────── */
  {
    slug: "financiamiento",
    exam_slug: "financing-529554",
    title: { es: "El truco del financiamiento", en: "The financing trick" },
    subtitle: {
      es: "Cómo el pago mensual convierte un \"muy caro\" en un \"sí, me lo llevo\".",
      en: "How monthly payments turn a \"too expensive\" into a \"yes, I'll take it\".",
    },
    cover_color: "#16a34a",
    accent: "#FFD100",
    category: "financing",
    pages: [
      {
        layout: "cover",
        title: { es: "El truco del financiamiento", en: "The financing trick" },
        subtitle: {
          es: "El precio total asusta. El pago mensual conecta.",
          en: "The total price scares. The monthly payment connects.",
        },
      },
      {
        layout: "panels-2",
        title: { es: "Cap. 1 — El cliente que se iba", en: "Ch. 1 — The customer walking out" },
        caption: {
          es: "El señor López mira el precio: $1,850. Suspira. Se voltea. Se dirige a la puerta.",
          en: "Mr. López sees the price: $1,850. Sighs. Turns around. Heads for the door.",
        },
        panels: [
          {
            speaker: "narrator",
            bg: "#ffefef",
            text: {
              es: "La mayoría de vendedores lo dejan ir. \"Muy caro\", piensan. Ana no.",
              en: "Most sellers let him leave. \"Too expensive\", they think. Not Ana.",
            },
          },
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.5,
            text: {
              es: "Señor López, antes de que se vaya… ¿me deja mostrarle algo en 30 segundos?",
              en: "Mr. López, before you leave… may I show you something in 30 seconds?",
            },
          },
        ],
        lesson: {
          variant: "did_you_know",
          title: { es: "¿SABÍAS QUÉ?", en: "DID YOU KNOW?" },
          text: {
            es: "El 60% de las ventas perdidas se recuperan cuando ofreces financiamiento ANTES del \"gracias, vuelvo\".",
            en: "60% of lost sales are recovered when you offer financing BEFORE the \"thanks, I'll come back\".",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 2 — Traducir el número grande", en: "Ch. 2 — Translating the big number" },
        caption: {
          es: "Ana saca la calculadora, pero no calcula: TRADUCE.",
          en: "Ana takes out her calculator, but doesn't calculate: she TRANSLATES.",
        },
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.4,
            text: {
              es: "$1,850 suena grande. Pero son $77 al mes por 24 meses. ¿Cuánto gasta usted en café a la semana?",
              en: "$1,850 sounds big. But it's $77 a month for 24 months. How much do you spend on coffee a week?",
            },
          },
          {
            speaker: "narrator",
            bg: "#fff",
            text: {
              es: "El señor López sonríe: \"unos $20\". Ana asiente: \"esto es café, señor. Un café al mes.\"",
              en: "Mr. López smiles: \"about $20\". Ana nods: \"this is coffee, sir. One coffee a month.\"",
            },
          },
          {
            speaker: "carlos",
            bg: "#ffefef",
            tilt: 0.5,
            text: {
              es: "¡Compara con algo que ya gasta! No pelees con el número. Ponlo en contexto.",
              en: "Compare it to something they already spend! Don't fight the number. Contextualize it.",
            },
          },
        ],
        lesson: {
          variant: "remember",
          title: { es: "RECUERDA", en: "REMEMBER" },
          text: {
            es: "Divide el total en semanas y compara con cosas cotidianas: café, gasolina, streaming.",
            en: "Break the total into weeks and compare to daily items: coffee, gas, streaming.",
          },
        },
      },
      {
        layout: "panels-2",
        title: { es: "Cap. 3 — Aprobación en 2 minutos", en: "Ch. 3 — Approval in 2 minutes" },
        caption: {
          es: "Ana llena el pre-check en su tablet mientras habla del sofá. Sin interrogatorio, sin miedo.",
          en: "Ana runs the pre-check on her tablet while they chat about the sofa. No interrogation, no fear.",
        },
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.5,
            text: {
              es: "Solo su ID y últimos 4 del seguro social. En 2 minutos vemos si califica y para cuánto.",
              en: "Just your ID and last 4 of your SSN. In 2 minutes we see if you qualify and for how much.",
            },
          },
          {
            speaker: "narrator",
            bg: "#fff",
            text: {
              es: "El proceso rápido baja la ansiedad. La palabra \"pre-aprobado\" hace magia en el cerebro.",
              en: "The fast process lowers anxiety. The word \"pre-approved\" works magic in the brain.",
            },
          },
        ],
        lesson: {
          variant: "common_error",
          title: { es: "ERROR COMÚN", en: "COMMON MISTAKE" },
          text: {
            es: "Nunca digas \"vamos a ver si el banco lo aprueba\". Di: \"vamos a ver para cuánto está pre-aprobado\".",
            en: "Never say \"let's see if the bank approves you\". Say: \"let's see how much you're pre-approved for\".",
          },
        },
      },
      {
        layout: "big-lesson",
        title: { es: "Cap. 4 — El sí que cambia el día", en: "Ch. 4 — The yes that changes the day" },
        caption: {
          es: "Aprobado. Sala entera. Entrega el sábado. El señor López sale con más de lo que pensaba comprar.",
          en: "Approved. Full living room. Saturday delivery. Mr. López leaves with more than he planned to buy.",
        },
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.4,
            text: {
              es: "$120 al mes por la sala completa. Y estrenamos el sábado. ¿Prefiere entrega en la mañana o tarde?",
              en: "$120/month for the full living room. Delivery Saturday. Morning or afternoon?",
            },
          },
        ],
        cta: {
          es: "El financiamiento no es un extra: es el puente que convierte \"muy caro\" en \"lo estrenamos este fin\".",
          en: "Financing isn't extra: it's the bridge that turns \"too expensive\" into \"we enjoy it this weekend\".",
        },
        lesson: {
          variant: "important",
          title: { es: "REGLA DE ORO", en: "GOLDEN RULE" },
          text: {
            es: "Ofrece financiamiento SIEMPRE, aunque el cliente no lo pida. Puede triplicar el ticket promedio.",
            en: "Always offer financing, even if not asked. It can triple your average ticket.",
          },
        },
      },
    ],
  },

  /* ─────────────────────  ISSUE 4 · TEMPUR-PEDIC  ───────────────────── */
  {
    slug: "tempur-pedic",
    exam_slug: "tempur-pedic-exam-adapt-collection-2ef349",
    title: { es: "La noche que Ana durmió como Rey", en: "The night Ana slept like royalty" },
    subtitle: {
      es: "Por qué la colección Adapt vale cada centavo.",
      en: "Why the Adapt collection is worth every penny.",
    },
    cover_color: "#0d1a3f",
    accent: "#E3000F",
    category: "tempur",
    pages: [
      {
        layout: "cover",
        title: { es: "La noche que Ana durmió como Rey", en: "The night Ana slept like royalty" },
        subtitle: {
          es: "Cómo explicar Tempur-Adapt en 60 segundos sin hablar de espuma.",
          en: "How to pitch Tempur-Adapt in 60 seconds without talking foam.",
        },
      },
      {
        layout: "panels-2",
        title: { es: "Cap. 1 — La cliente cansada", en: "Ch. 1 — The exhausted customer" },
        caption: {
          es: "La señora Martínez lleva 6 meses despertándose con dolor de espalda. Su esposo ronca. Necesitan solución.",
          en: "Mrs. Martínez has woken with back pain for 6 months. Her husband snores. They need a solution.",
        },
        panels: [
          {
            speaker: "narrator",
            bg: "#ffefef",
            text: {
              es: "Ana no dice \"tenemos colchones\". Ana escucha primero.",
              en: "Ana doesn't say \"we have mattresses\". Ana listens first.",
            },
          },
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.5,
            text: {
              es: "¿Se despiertan con dolor? ¿Sienten cada vez que el otro se mueve? Cuénteme.",
              en: "Do you wake up sore? Feel every time your partner moves? Tell me.",
            },
          },
        ],
        lesson: {
          variant: "remember",
          title: { es: "DISCOVER PRIMERO", en: "DISCOVER FIRST" },
          text: {
            es: "Nunca vendas Tempur sin descubrir el DOLOR real (espalda, calor, movimiento del otro).",
            en: "Never sell Tempur without discovering the real PAIN (back, heat, partner motion).",
          },
        },
      },
      {
        layout: "panels-3",
        title: { es: "Cap. 2 — F.A.B. de Tempur-Adapt", en: "Ch. 2 — Tempur-Adapt F.A.B." },
        caption: {
          es: "Ana traduce la tecnología en beneficios reales para ELLA, no discurso técnico.",
          en: "Ana translates the tech into real benefits for HER, not a tech pitch.",
        },
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.5,
            text: {
              es: "Tempur-Adapt tiene material que responde al calor de tu cuerpo (F). Se moldea a tu columna (A). Te despiertas SIN dolor de espalda (B).",
              en: "Tempur-Adapt has material that responds to body heat (F). It molds to your spine (A). You wake up WITHOUT back pain (B).",
            },
          },
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: 0.4,
            text: {
              es: "Y absorbe el movimiento (F). Si su esposo se mueve, usted no lo siente (A). Duermen los dos toda la noche (B).",
              en: "And it absorbs motion (F). If your husband moves, you don't feel it (A). Both of you sleep through the night (B).",
            },
          },
          {
            speaker: "carlos",
            bg: "#ffefef",
            tilt: -0.4,
            text: {
              es: "¡Nunca hables solo de \"memory foam\"! Habla de la vida de la cliente después de comprar.",
              en: "Never sell just \"memory foam\"! Sell the customer's life AFTER they buy.",
            },
          },
        ],
        lesson: {
          variant: "important",
          title: { es: "TEMPUR-ADAPT", en: "TEMPUR-ADAPT" },
          text: {
            es: "Puntos clave: alivio de presión, respuesta al calor corporal, absorción de movimiento, 10 años de garantía.",
            en: "Key points: pressure relief, body-heat response, motion absorption, 10-year warranty.",
          },
        },
      },
      {
        layout: "panels-2",
        title: { es: "Cap. 3 — La prueba de 90 noches", en: "Ch. 3 — The 90-night trial" },
        caption: {
          es: "El precio los frena. Ana usa la garantía como red de seguridad.",
          en: "The price stops them. Ana uses the warranty as a safety net.",
        },
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.3,
            text: {
              es: "Tempur da 90 noches de prueba. Si en 3 meses no duermen mejor, se lo cambiamos. Sin drama.",
              en: "Tempur offers a 90-night trial. If in 3 months you don't sleep better, we swap it. No drama.",
            },
          },
          {
            speaker: "narrator",
            bg: "#fff",
            text: {
              es: "La sensación de \"cero riesgo\" es la que cierra la venta premium.",
              en: "The feeling of \"zero risk\" closes the premium sale.",
            },
          },
        ],
        lesson: {
          variant: "did_you_know",
          title: { es: "¿SABÍAS QUÉ?", en: "DID YOU KNOW?" },
          text: {
            es: "90% de los clientes Tempur KEEP el colchón después de la prueba. La devolución es rara.",
            en: "90% of Tempur customers KEEP the mattress after the trial. Returns are rare.",
          },
        },
      },
      {
        layout: "big-lesson",
        title: { es: "Cap. 4 — Una semana después", en: "Ch. 4 — One week later" },
        caption: {
          es: "La señora Martínez llama a Ana. \"Dormí 8 horas seguidas. No sabía que era posible\".",
          en: "Mrs. Martínez calls Ana. \"I slept 8 straight hours. I didn't know it was possible\".",
        },
        panels: [
          {
            speaker: "ana",
            bg: "#eaf3ff",
            tilt: -0.4,
            text: {
              es: "Señora, me alegra tanto. Cualquier duda o si quieren la base ajustable, aquí estoy.",
              en: "Ma'am, I'm so glad. Any questions, or if you want the adjustable base, I'm right here.",
            },
          },
        ],
        cta: {
          es: "Descubre el dolor → Traduce con F.A.B. → Elimina el miedo con la garantía → Follow-up siempre.",
          en: "Discover the pain → Translate with F.A.B. → Kill fear with the warranty → Always follow up.",
        },
        lesson: {
          variant: "remember",
          title: { es: "LA REGLA TEMPUR", en: "THE TEMPUR RULE" },
          text: {
            es: "Nunca vendas colchón. Vende el domingo sin dolor de espalda, la noche sin roncar del otro.",
            en: "Never sell a mattress. Sell the Sunday without back pain, the night without partner snoring.",
          },
        },
      },
    ],
  },
];

// Merge the leadership comic (Manager) at the front with the rest of the classic
// issues so the reader shows Manager first when logged as manager, but findComic()
// still finds every slug.
COMICS.push(...OTHER_COMICS_START);

export const findComic = (slug) => COMICS.find((c) => c.slug === slug);

/* ─────────────────────────── INTERACTIVE QUIZZES ───────────────────────────
   One mini "¿Qué haría Ana?" question per non-cover page. The reader shows
   it AFTER the reader taps "¿Y ahora?" so it feels like an interactive beat,
   not a test. Choices are: label + is_correct + reaction (Carlos/Ana comment).
──────────────────────────────────────────────────────────────────────────── */
export const INTERACTIVE_QUIZZES = {
  // ─── VENTAS ─────────────────────────────────────────────
  "ventas-1": {
    prompt: { es: "La familia entra. ¿Cuál es el mejor saludo?", en: "The family walks in. What's the best greeting?" },
    choices: [
      { es: "¿Le puedo ayudar en algo?", en: "Can I help you with something?", correct: false, reaction: { es: "El cliente contesta 'no' por reflejo. Perdiste el saludo.", en: "The customer reflex-answers 'no'. Lost greeting." } },
      { es: "¡Buenos días! Soy Ana, ¿cómo se llaman?", en: "Good morning! I'm Ana, what are your names?", correct: true, reaction: { es: "¡Perfecto! Bajaste la guardia en 5 segundos.", en: "Perfect! Guard dropped in 5 seconds." } },
      { es: "Los sofás están al fondo.", en: "The sofas are in the back.", correct: false, reaction: { es: "Directo al producto sin conexión humana. Los pierdes.", en: "Straight to product with no connection. You lose them." } },
    ],
  },
  "ventas-2": {
    prompt: { es: "¿Qué pregunta abierta funciona mejor?", en: "Which open question works best?" },
    choices: [
      { es: "¿Quiere ver ofertas?", en: "Want to see deals?", correct: false, reaction: { es: "Es un sí/no. No descubres nada.", en: "It's yes/no. You learn nothing." } },
      { es: "¿Para qué habitación es y cuántos son en casa?", en: "Which room is it for and how many at home?", correct: true, reaction: { es: "¡Sí! Descubres el uso real. Vas a poder hablar de beneficios.", en: "Yes! You learn the real use. You can now sell benefits." } },
      { es: "¿Cuánto piensa gastar?", en: "How much do you want to spend?", correct: false, reaction: { es: "Nunca preguntes precio primero. Levantas defensa.", en: "Never lead with price. It raises defenses." } },
    ],
  },
  "ventas-3": {
    prompt: { es: "Tela impermeable = Feature. ¿Cuál es el BENEFICIO?", en: "Waterproof fabric = Feature. What's the BENEFIT?" },
    choices: [
      { es: "Está hecho con poliéster tratado.", en: "It's made with treated polyester.", correct: false, reaction: { es: "Eso sigue siendo característica.", en: "That's still a feature." } },
      { es: "No se mancha con jugo.", en: "It doesn't stain from juice.", correct: false, reaction: { es: "Eso es la ventaja. Falta subir un nivel más.", en: "That's the advantage. One more level up." } },
      { es: "Sus noches de películas serán sin miedos.", en: "Your movie nights will be worry-free.", correct: true, reaction: { es: "¡Beneficio puro! Le vendes su vida, no la tela.", en: "Pure benefit! You sold their life, not the fabric." } },
    ],
  },
  "ventas-4": {
    prompt: { es: "La familia duda. ¿Qué haces?", en: "The family hesitates. What do you do?" },
    choices: [
      { es: "\"¿Lo quiere sí o no?\"", en: "\"Yes or no?\"", correct: false, reaction: { es: "Modo defensa activado. Vas a perder la venta.", en: "Defense mode on. You'll lose the sale." } },
      { es: "\"Le bajo $200 y ya.\"", en: "\"I'll cut $200 and that's it.\"", correct: false, reaction: { es: "Cediste sin necesidad. Ganancia perdida.", en: "You caved for no reason. Lost margin." } },
      { es: "\"¿Entrega el jueves o el sábado?\"", en: "\"Delivery Thursday or Saturday?\"", correct: true, reaction: { es: "¡Cierre alternativo perfecto!", en: "Perfect alternative close!" } },
    ],
  },
  "ventas-5": {
    prompt: { es: "48h después de la entrega, ¿qué debes hacer?", en: "48h after delivery, what should you do?" },
    choices: [
      { es: "Esperar a que llame el cliente.", en: "Wait for them to call.", correct: false, reaction: { es: "El cliente no llama. Se olvida.", en: "They won't. They'll forget." } },
      { es: "Llamar y preguntar cómo van con el mueble.", en: "Call and check how the piece is working.", correct: true, reaction: { es: "¡Follow-up de campeones! Cliente feliz = 3 referidos.", en: "Champion follow-up! Happy customer = 3 referrals." } },
      { es: "Mandar una promo por texto.", en: "Send a promo text.", correct: false, reaction: { es: "Se siente comercial. Pierdes el vínculo.", en: "Feels commercial. You lose the bond." } },
    ],
  },

  // ─── SERVICIO ─────────────────────────────────────────────
  "servicio-1": {
    prompt: { es: "Cliente furioso golpea el mostrador. Lo primero:", en: "Furious customer slams the counter. First move:" },
    choices: [
      { es: "Defender a la tienda: \"no fue nuestra culpa\".", en: "Defend the store: \"wasn't our fault\".", correct: false, reaction: { es: "El fuego crece. Enorme error.", en: "Fire grows. Huge mistake." } },
      { es: "Escuchar sin interrumpir.", en: "Listen without interrupting.", correct: true, reaction: { es: "¡Correcto! Sin ser escuchado no hay solución.", en: "Right! Without listening, no resolution." } },
      { es: "Llamar al gerente inmediatamente.", en: "Call the manager right now.", correct: false, reaction: { es: "Delegas el problema y el cliente se enfría… mal.", en: "You dump the problem — customer feels ignored." } },
    ],
  },
  "servicio-2": {
    prompt: { es: "¿Cómo te disculpas?", en: "How do you apologize?" },
    choices: [
      { es: "\"Fue la fábrica, no yo.\"", en: "\"It was the factory, not me.\"", correct: false, reaction: { es: "Culpa a otros. El cliente pierde la confianza.", en: "You blame others. Trust dies." } },
      { es: "\"Tiene razón. Me disculpo en nombre de Canales.\"", en: "\"You're right. I apologize on behalf of Canales.\"", correct: true, reaction: { es: "Te haces cargo. Ahora sí puedes resolver.", en: "You own it. Now you can solve it." } },
      { es: "\"Todo el mundo tiene ese problema.\"", en: "\"Everyone has that problem.\"", correct: false, reaction: { es: "Minimizas y ofendes.", en: "You minimize and offend." } },
    ],
  },
  "servicio-3": {
    prompt: { es: "¿Qué solución ofreces?", en: "What solution do you offer?" },
    choices: [
      { es: "\"Vuelva la próxima semana a ver qué se puede.\"", en: "\"Come back next week and we'll see.\"", correct: false, reaction: { es: "Sin fecha, sin acción. Perdiste al cliente.", en: "No date, no action. Customer lost." } },
      { es: "\"Cambio inmediato + 15% en su próxima compra.\"", en: "\"Instant swap + 15% off next purchase.\"", correct: true, reaction: { es: "Acción concreta. Fidelizas para siempre.", en: "Concrete action. Loyalty for life." } },
      { es: "\"Le regalo un cojín.\"", en: "\"I'll toss in a cushion.\"", correct: false, reaction: { es: "No resuelve el dolor real.", en: "Doesn't fix the real pain." } },
    ],
  },
  "servicio-4": {
    prompt: { es: "Un mes después vuelve la señora Ramírez. ¿Qué hacer?", en: "A month later Mrs. Ramírez returns. What now?" },
    choices: [
      { es: "Recordar su historia y saludarla por nombre.", en: "Remember her story and greet by name.", correct: true, reaction: { es: "¡Fidelidad total! Trae a su hermana.", en: "Full loyalty! She brought her sister." } },
      { es: "Actuar como si no la conocieras.", en: "Pretend you don't know her.", correct: false, reaction: { es: "Se siente irrelevante. Se va sin comprar.", en: "She feels irrelevant. Leaves empty-handed." } },
      { es: "Ofrecerle solo lo más barato.", en: "Offer her only cheap items.", correct: false, reaction: { es: "La subestimas. Prejuicio malo.", en: "You underestimate her. Bad bias." } },
    ],
  },

  // ─── FINANCIAMIENTO ─────────────────────────────────────────────
  "financiamiento-1": {
    prompt: { es: "El cliente ve $1,850 y se voltea a la puerta. ¿Qué haces?", en: "Customer sees $1,850 and turns to the door. What do you do?" },
    choices: [
      { es: "Dejarlo ir, \"muy caro para él\".", en: "Let him go, \"too expensive\".", correct: false, reaction: { es: "60% de esas ventas se recuperan con financiamiento. Perdiste.", en: "60% of those sales come back with financing. Lost sale." } },
      { es: "Ofrecerle ver algo en 30 segundos.", en: "Ask for 30 seconds to show something.", correct: true, reaction: { es: "¡Ganaste tiempo! Ahora traduce el número.", en: "You bought time! Now translate the number." } },
      { es: "Bajarle el precio a $1,500.", en: "Cut price to $1,500.", correct: false, reaction: { es: "Cediste sin pelear. Y sigue sin comprar.", en: "You caved with no fight. Still no sale." } },
    ],
  },
  "financiamiento-2": {
    prompt: { es: "¿Cómo hablas del pago mensual?", en: "How do you frame the monthly payment?" },
    choices: [
      { es: "\"Son $77 al mes.\"", en: "\"It's $77/month.\"", correct: false, reaction: { es: "Correcto pero abstracto. Falta el gancho.", en: "Correct but abstract. Missing the hook." } },
      { es: "\"$77/mes — un café al mes de lo que ya gasta.\"", en: "\"$77/month — one month of your coffee spend.\"", correct: true, reaction: { es: "¡Compara con algo real! Ahora tiene sentido.", en: "Real-life comparison! Now it clicks." } },
      { es: "\"Es baratísimo, no piense tanto.\"", en: "\"It's dirt cheap, don't overthink.\"", correct: false, reaction: { es: "Presión y desprecio. Terrible.", en: "Pressure + condescension. Terrible." } },
    ],
  },
  "financiamiento-3": {
    prompt: { es: "¿Qué palabra evitas al hablar del banco?", en: "What word do you avoid when talking about the lender?" },
    choices: [
      { es: "\"Aprobado\"", en: "\"Approved\"", correct: false, reaction: { es: "Al contrario, sí queremos esa palabra.", en: "Opposite — we want this word." } },
      { es: "\"Pre-aprobado\"", en: "\"Pre-approved\"", correct: false, reaction: { es: "También la queremos.", en: "We want this one too." } },
      { es: "\"A ver si el banco lo aprueba…\"", en: "\"Let's see if the bank approves you…\"", correct: true, reaction: { es: "¡Bien! Nunca sembres esa duda.", en: "Right! Never plant that doubt." } },
    ],
  },
  "financiamiento-4": {
    prompt: { es: "Está aprobado. Cierre alternativo:", en: "He's approved. Alternative close:" },
    choices: [
      { es: "\"¿Se lo lleva o no?\"", en: "\"Take it or not?\"", correct: false, reaction: { es: "Ultimátum. Defensa.", en: "Ultimatum. Defense on." } },
      { es: "\"¿Entrega en la mañana o en la tarde el sábado?\"", en: "\"Saturday morning or afternoon delivery?\"", correct: true, reaction: { es: "Ambas ganan. ¡Cierre!", en: "Both win. Closed!" } },
      { es: "\"Piénselo con la almohada.\"", en: "\"Sleep on it.\"", correct: false, reaction: { es: "Sabotaje. Se va y no vuelve.", en: "Self-sabotage. He'll never return." } },
    ],
  },

  // ─── TEMPUR-PEDIC ─────────────────────────────────────────────
  "tempur-pedic-1": {
    prompt: { es: "La cliente se queja de dolor de espalda. Primero:", en: "Customer complains of back pain. First:" },
    choices: [
      { es: "Enseñarle el modelo más caro.", en: "Show the priciest model.", correct: false, reaction: { es: "No descubriste el dolor real. Rebote.", en: "You skipped discovery. Bounce." } },
      { es: "Preguntar cómo duerme y qué siente al despertar.", en: "Ask how she sleeps and how she wakes.", correct: true, reaction: { es: "Discovery primero. El dolor guía la venta.", en: "Discovery first. Pain guides the pitch." } },
      { es: "Hablarle de memory foam y densidad.", en: "Pitch memory foam and density.", correct: false, reaction: { es: "Tecnicismos sin dolor conectado. No conecta.", en: "Tech-talk without pain link. No connection." } },
    ],
  },
  "tempur-pedic-2": {
    prompt: { es: "El beneficio F.A.B. de Tempur-Adapt es:", en: "The F.A.B. benefit of Tempur-Adapt is:" },
    choices: [
      { es: "Material que responde al calor corporal.", en: "Heat-responsive material.", correct: false, reaction: { es: "Eso es Feature.", en: "That's a feature." } },
      { es: "Se moldea a tu columna.", en: "Molds to your spine.", correct: false, reaction: { es: "Eso es Advantage.", en: "That's an advantage." } },
      { es: "Te despiertas SIN dolor de espalda.", en: "You wake up WITHOUT back pain.", correct: true, reaction: { es: "¡Beneficio real! Le vendes su vida.", en: "Real benefit! You sell her life." } },
    ],
  },
  "tempur-pedic-3": {
    prompt: { es: "El precio los frena. ¿Qué garantía uses?", en: "Price stops them. Which guarantee do you use?" },
    choices: [
      { es: "\"Confíe en mí, le va a gustar.\"", en: "\"Trust me, you'll love it.\"", correct: false, reaction: { es: "Sin garantía sólida. No cierra.", en: "No solid guarantee. No close." } },
      { es: "\"Tempur da 90 noches de prueba.\"", en: "\"Tempur offers a 90-night trial.\"", correct: true, reaction: { es: "¡Cero riesgo! Ese sentimiento cierra premium.", en: "Zero risk! That feeling closes premium." } },
      { es: "\"Los otros colchones son peores.\"", en: "\"Other mattresses are worse.\"", correct: false, reaction: { es: "Hablar mal de competencia baja tu marca.", en: "Trash-talking rivals lowers your brand." } },
    ],
  },
  "tempur-pedic-4": {
    prompt: { es: "Una semana después llama. ¿Qué le ofreces?", en: "A week later she calls. What next?" },
    choices: [
      { es: "Nada, ya vendiste.", en: "Nothing, sale's done.", correct: false, reaction: { es: "Dejas dinero en la mesa.", en: "You leave money on the table." } },
      { es: "La base ajustable y almohadas Tempur.", en: "The adjustable base and Tempur pillows.", correct: true, reaction: { es: "¡Ticket promedio +40%! Cross-sell natural.", en: "Ticket +40%! Natural cross-sell." } },
      { es: "Pedirle una reseña de 5 estrellas.", en: "Ask for a 5-star review.", correct: false, reaction: { es: "Muy pronto. Primero da valor.", en: "Too soon. Deliver value first." } },
    ],
  },
};

export const getInteractive = (slug, pageIndex) =>
  INTERACTIVE_QUIZZES[`${slug}-${pageIndex}`] || null;

/**
 * When a page has real panels but no explicit `scenes` array, auto-derive
 * scenes at render time so every multi-panel page gets a real multi-panel
 * layout. Each panel becomes one scene with a Nano-Banana-friendly prompt
 * built from the page caption + panel dialogue.
 */
export const deriveScenes = (comic, page, pageIndex) => {
  const panels = (page.panels || []).filter(Boolean);
  const capES = page.caption?.es || "";
  const capEN = page.caption?.en || "";
  const category = comic.category;
  const flavor = {
    sales: "warm Muebleria Canales showroom, comic-book style, halftone dots",
    service: "empathetic customer service moment inside Muebleria Canales, comic-book style",
    financing: "confident financial advice inside Muebleria Canales, comic-book style",
    tempur: "premium Canales mattress showroom, comic-book style, blue palette",
  }[category] || "Muebleria Canales showroom, comic-book style";

  // If the page already has explicit scenes, honor them as-is.
  if (Array.isArray(page.scenes) && page.scenes.length > 0) return page.scenes;

  // Otherwise, derive one scene per panel (need at least 2 panels).
  if (panels.length < 2) return [];
  const scenes = panels.map((p, i) => {
    const who =
      p.speaker === "ana"
        ? "Ana (young Latina saleswoman in red polo)"
        : p.speaker === "carlos"
        ? "Carlos (Latino mentor in blue polo)"
        : p.speaker === "mascot"
        ? "the blue smiling sofa mascot"
        : "the customer(s)";
    const dialog = p.text?.es || p.text?.en || "";
    return {
      auto: true,
      prompt:
        `Comic panel ${i + 1} of "${comic.title.es}". ${capES ? "Context: " + capES + " " : ""}` +
        `Focus on ${who}${dialog ? ` who is thinking/saying: "${dialog.slice(0, 120)}"` : ""}. ` +
        `Wide framing with the character positioned on ONE side of the frame, ` +
        `leaving the opposite upper corner mostly empty (sky, wall, ceiling, showroom background) ` +
        `so a comic speech bubble can sit there without covering any face. ` +
        `${flavor}. No text or bubbles inside the image.`,
      panel: p.speaker && p.speaker !== "narrator" ? {
        speaker: p.speaker,
        text: p.text,
        tail: i % 2 === 0 ? "left" : "right",
      } : null,
      caption: p.speaker === "narrator" ? p.text : null,
      // fallback captions if narrator not present
      _captions: { es: capES, en: capEN },
    };
  });

  // Ensure every non-cover page has AT LEAST 6 scenes (2×3 flyer grid) with
  // REAL AI prompts on each — so the admin regen paints all six panels, no
  // more empty placeholder tiles. Padded scenes get varied reaction/action
  // prompts so Nano Banana produces different compositions per tile.
  const REACTION_FLAVORS = [
    "wide reaction shot from an over-the-shoulder angle showing the group's response",
    "medium close-up on the customer's face reacting with a smile, salesperson visible on the side",
    "wide establishing shot of the Canales showroom aisle with the characters walking together",
    "medium two-shot of the customers exchanging a hopeful look while the salesperson gestures",
    "action shot of the salesperson handing a brochure or clipboard to the customer",
    "warm hero shot with the family standing next to their chosen piece, salesperson smiling nearby",
  ];
  // Cycle real panel dialogue into padded scenes so each tile carries its own
  // (short) beat instead of repeating the page-level caption. Padded scenes
  // set `caption: null` because the yellow caption strip already lives at the
  // top of the sheet — repeating it inside every extra tile made the flyer
  // look broken.
  const REACTION_BUBBLES_ES = [
    "¡Vamos equipo!",
    "Estoy contigo.",
    "Cerramos hoy.",
    "Bien pensado.",
    "Sí se puede.",
    "Excelente idea.",
  ];
  const REACTION_BUBBLES_EN = [
    "Let's go team!",
    "I'm with you.",
    "We close today.",
    "Good thinking.",
    "We got this.",
    "Great idea.",
  ];
  while (scenes.length < 6) {
    const idx = scenes.length;
    const lastDialog = panels[panels.length - 1]?.text?.es || capES;
    const speaker = panels[idx % panels.length]?.speaker || "ana";
    const cyclePanel = speaker && speaker !== "narrator"
      ? {
          speaker,
          text: {
            es: REACTION_BUBBLES_ES[idx % REACTION_BUBBLES_ES.length],
            en: REACTION_BUBBLES_EN[idx % REACTION_BUBBLES_EN.length],
          },
          tail: idx % 2 === 0 ? "left" : "right",
        }
      : null;
    scenes.push({
      auto: true,
      prompt:
        `Reaction panel ${idx + 1} of "${comic.title.es}". ${capES ? capES + " " : ""}` +
        `${REACTION_FLAVORS[idx % REACTION_FLAVORS.length]}. ` +
        `${lastDialog ? `Emotional beat tied to: "${lastDialog.slice(0, 90)}". ` : ""}` +
        `Characters stand on ONE side of the frame with the opposite top corner intentionally ` +
        `empty (background wall, ceiling, showroom sign) so a small speech bubble can sit there ` +
        `without covering any face. ${flavor}. No text or bubbles inside the image.`,
      panel: cyclePanel,
      caption: null,
      _pad: true,
    });
  }
  return scenes;
};

/**
 * Build a Nano-Banana-friendly Spanish scene prompt for one comic page.
 * We deliberately keep it short (title + caption + speaker actions) so the
 * model focuses on composition and doesn't try to render text.
 */
export const buildPagePrompt = (comic, page, pageIndex) => {
  const category = comic.category;
  const parts = [];
  parts.push(`Comic-book page ${pageIndex + 1} of the story "${comic.title.es}".`);
  if (page.layout === "cover") {
    parts.push(
      `Full-bleed magazine cover illustration for "${comic.title.es}". ` +
      `Subtitle mood: ${comic.subtitle.es}. ` +
      `Feature Ana (young Latina saleswoman, red polo, ponytail) and Carlos ` +
      `(Latino mentor, blue polo, warm smile) posing heroically inside a Muebleria Canales showroom.`
    );
  } else {
    if (page.title?.es) parts.push(`Chapter: ${page.title.es}.`);
    if (page.caption?.es) parts.push(`Scene: ${page.caption.es}`);
    (page.panels || []).forEach((p, i) => {
      const who =
        p.speaker === "ana"
          ? "Ana (young Latina saleswoman in red polo)"
          : p.speaker === "carlos"
          ? "Carlos (Latino mentor in blue polo)"
          : p.speaker === "mascot"
          ? "the blue smiling sofa mascot"
          : "the narrator viewpoint";
      const action = p.action?.es ? ` — ${p.action.es}` : "";
      const text = p.text?.es ? ` shows: ${p.text.es.slice(0, 120)}` : "";
      parts.push(`Panel ${i + 1}: ${who}${text}${action}.`);
    });
  }
  // category flavor — includes cast-mix hints so different chapters showcase
  // different real Canales team members without breaking recognizability.
  const flavor = {
    sales: "Bright hopeful family-shopping vibe inside Mueblería Canales. The García family (dad blue button-down, mom green polo with gold hoops, daughter pink heart shirt) explores the showroom guided by Lupita (long wavy brown hair with caramel highlights, royal blue Canales polo with gold logo) — sometimes joined by Ana (glasses, ponytail) in the background.",
    service: "Empathetic customer-service moment inside Mueblería Canales. Sofía (dark straight hair, royal blue Canales polo, gold hoops) listens calmly to the García family in a bright showroom aisle; Lupita nearby.",
    financing: "Confident financial-advice moment inside Mueblería Canales. Lupita (long wavy caramel-highlighted hair, royal blue Canales polo with gold logo) holds a 'SOLICITUD DE FINANCIAMIENTO' clipboard next to the García family, walking them through a friendly step-by-step process.",
    tempur: "Cozy Canales mattress showroom moment. Lupita (long wavy caramel-highlighted hair, royal blue Canales polo) gently guides the family through the Tempur bed line — focus on rest, comfort and a blue palette.",
    manager: "Confident professional leadership vibe with Carlos (mid-40s Latino, short buzz-cut, mustache-goatee, official SOLID BLACK Canales manager polo with gold logo) coaching Lupita and Ana (royal blue polos) on the showroom floor. Warm ceiling lights, real customers in the background.",
    allstate: "Trust-and-protection moment inside Canales. Lupita (long wavy caramel-highlighted hair, royal blue Canales polo with gold logo) explains an Allstate protection plan to the García family, holding a small tablet or brochure; Roberto (mid-40s Latino, stubble goatee, black manager polo) walks past behind them.",
  }[category] || "";
  if (flavor) parts.push(flavor);
  // Composition rule so the overlaid speech bubble never sits on a face:
  parts.push(
    "COMPOSITION: keep all characters on one side of the frame; leave the OPPOSITE upper corner " +
    "(top-left OR top-right, roughly 35% wide × 45% tall) fully empty — plain wall, ceiling, sky " +
    "or showroom background only — so a comic speech bubble can be placed there without covering " +
    "any face, hand or body. Never crop faces at the top of the frame."
  );
  return parts.join(" ");
};
